type LocaleContext = {
  locale?: string;
  defaultLocale?: string;
};

// Footer hrefs come from Lokalise as market-relative paths ("/about-us"), and
// the footer renders them into a plain <a> rather than next/link — so nothing
// adds the locale prefix Next serves a market's secondary tree under. On
// lassie.co the English about page is /en/about-us, while on the UK site (where
// en IS the default locale) it stays /about-us. Same rule as useCanonicalUrl.
//
// Without this, one shared en_GB value cannot be right for both, which is why
// the Lokalise keys grew `_gbr` overrides that differ only by the prefix.
export const localeHref = (
  href: string,
  { locale, defaultLocale }: LocaleContext
): string => {
  if (!href) {
    return "";
  }

  // Document links, app stores and cross-domain URLs are absolute already.
  if (/^([a-z]+:)?\/\//i.test(href) || /^(mailto|tel):/i.test(href)) {
    return href;
  }

  const path = href.startsWith("/") ? href : `/${href}`;

  if (!locale || !defaultLocale || locale === defaultLocale) {
    return path;
  }

  // The secondary tree's root is served at /<locale>, with no trailing slash —
  // a "/" href would otherwise link to /<locale>/, which redirects.
  const withoutTrailingSlash = (value: string) =>
    value.length > 1 ? value.replace(/\/$/, "") : value;

  // Tolerate values that already carry the prefix, so this is safe to ship
  // before or after the Lokalise values are simplified. Guard on the segment
  // boundary — "/energy" must not read as an already-prefixed "en" path.
  if (path === `/${locale}` || path.startsWith(`/${locale}/`)) {
    return withoutTrailingSlash(path);
  }

  return withoutTrailingSlash(`/${locale}${path}`);
};
